import clsx from 'clsx'
import Group from 'components/utils/Group'
import { UspsLayoutSizeContext, type UspTileSize } from 'components/Common/UspTile/UspTile'
import type { ComponentProps, ReactNode, Ref } from 'react'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'

const Tiles = styled(Group)`
  &.size-large > * {
    flex: 1 0 0;
  }

  &.size-responsive {
    ${mediaQueryUp.desktop} {
      > * {
        flex: 1 0 0;
      }
    }
  }
`

const SectionHeading = styled(Heading)`
  text-wrap: balance;

  &.size-responsive {
    ${mediaQueryUp.desktop} {
      text-align: center;
    }
  }
`

const Actions = styled(Group)`
  &.size-responsive > * {
    width: 100%;

    ${mediaQueryUp.desktop} {
      width: auto;
    }
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  ref?: Ref<HTMLDivElement>
  /**
   * `large` is a centred headline with equal-width tiles in a row.
   * `small` is a start-aligned headline with stacked tiles.
   * Nested `UspTile`s inherit this size unless they set their own.
   * When omitted, CSS switches at the desktop breakpoint so SSR and hydration match.
   */
  size?: UspTileSize
  heading?: ReactNode
  children?: ReactNode
  /**
   * Props for the primary action. `kind`, `size`, `shape` and `fit` are locked.
   */
  primaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'size' | 'shape' | 'fit'>
  /**
   * Props for the secondary action. `kind`, `size`, `shape` and `fit` are locked.
   */
  secondaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'size' | 'shape' | 'fit'>
  className?: string
}

function UspsBase({
  ref,
  size,
  heading,
  children,
  primaryActionButtonProps,
  secondaryActionButtonProps,
  className,
  ...rest
}: Props) {
  const isResponsive = size == null
  const isLarge = size === 'large'
  const sizeClassName = isResponsive ? 'size-responsive' : `size-${size}`
  const hasActions = primaryActionButtonProps != null || secondaryActionButtonProps != null
  const buttonFit = isResponsive || isLarge ? 'auto' : 'full-width'

  return <UspsLayoutSizeContext.Provider value={size ?? 'responsive'}>
    <Group
      {...rest}
      ref={ref}
      className={clsx(className, sizeClassName)}
      direction="vertical"
      gap={isLarge ? 32 : 20}
      desktopGap={isResponsive ? 32 : undefined}
      horizontalAlign="stretch"
      fullWidth
    >
      {heading != null && <SectionHeading
        className={isResponsive ? 'size-responsive' : undefined}
        variant="heading3"
        colour="normal-contrast"
        align={isLarge ? 'center' : 'start'}
        wrap="word-wrap"
      >
        {heading}
      </SectionHeading>}
      {children != null && <Tiles
        className={sizeClassName}
        direction={isLarge ? 'horizontal' : 'vertical'}
        desktopDirection={isResponsive ? 'horizontal' : undefined}
        gap={20}
        fullWidth
      >
        {children}
      </Tiles>}
      {hasActions && <Actions
        className={sizeClassName}
        direction={isLarge ? 'horizontal' : 'vertical'}
        desktopDirection={isResponsive ? 'horizontal' : undefined}
        gap={12}
        wrap={isLarge ? 'wrap' : undefined}
        desktopWrap={isResponsive ? 'wrap' : undefined}
        horizontalAlign={isLarge ? 'center' : 'stretch'}
        desktopHorizontalAlign={isResponsive ? 'center' : undefined}
        fullWidth
      >
        {primaryActionButtonProps && <TextButton
          {...primaryActionButtonProps}
          size="medium"
          shape="round"
          fit={buttonFit}
          kind="primary"
        />}
        {secondaryActionButtonProps && <TextButton
          {...secondaryActionButtonProps}
          size="medium"
          shape="round"
          fit={buttonFit}
          kind="tertiary"
        />}
      </Actions>}
    </Group>
  </UspsLayoutSizeContext.Provider>
}

export default UspsBase
